import { client } from '../libraries/axios/axios';
import { isInteger } from './typeUtils';

/**
 * Ensure the url is defined and is a string
 * @param {String} url URL to check
 * @throws if the url is not a string
 */
const checkUrlIsString = url => {
    if ((!url && url !== '') || (typeof url) !== 'string') {
        throw new Error(`Expected url to be a string but got ${JSON.stringify(url)}`);
    }
};

/**
 * Normalize serialized string literals ('null', 'undefined') to their actual values.
 * @param {*} value Value to sanitize
 * @returns {*}
 */
const sanitizeValue = (value) => {
    if (value === 'null') {
        return null;
    }
    if (value === 'undefined') {
        return undefined;
    }
    return value;
};

/**
 * Replace ":param" tokens in URL parts
 * @param {String} url URL to update
 * @param {Object} params Params to replace in the URL
 * @returns {String} URL with the given parameters
 * @throws if the url is not a string
 */
const replaceParameters = (url, params) => {
    checkUrlIsString(url);
    // Allow no params
    if (params) {
        const [paramsPart, queryPart] = url.split(/\?(.+)/);
        const paramsReplace = paramsPart
            .split('/')
            .map(partUrl => partUrl.indexOf(':') === 0 && params[partUrl.slice(1)] ? params[partUrl.slice(1)] : partUrl)
            .join('/');

        return queryPart ?
            paramsReplace.concat('?', queryPart)
            :
            paramsReplace;
    }
    return url;
};

/**
 * Add query parameters to the given URL
 * @param {String} url Base URL to add query parameters
 * @param {Object} query Parameters
 * @param {{key: String, desc: Boolean}[]} query.orders Array of orders to apply on the query
 * @param {{key: String, value: *}[]} query.filters Array of filters to apply on the query
 * @param {Number} query.limit Limit of the query
 * @param {Number} query.offset Offset of the query
 * @returns {String} URL with the query params
 */
const buildUrl = (url, { orders, filters, limit, offset, ...query } = {}) => {
    checkUrlIsString(url);

    const splitQuery = url.split('?');
    const queryPart = splitQuery.filter((urlPart, index) => index > 0).join('?');

    // Handle cases where we have :
    // url = /posTerminal?name=name with a ? inside&toto=truc
    // Store the global query in an array to use the Array.join function
    const resultQuery = queryPart ? [queryPart] : [];

    // Handle orders
    if (orders && orders.length > 0) {
        const ordersArray = orders.map(order => `${order.key} ${order.desc ? 'desc' : 'asc'}`);
        resultQuery.push(`order=${ordersArray.join(',')}`);
    }

    // Handle filters
    if (filters && filters.length > 0) {
        filters.forEach(filter => {
            const simOfferValues = filter.value &&
                (filter.value.tariffDataValue || filter.value.tariffDataUnitValue || filter.value.tariffNameValue) ? [
                filter.value.tariffDataValue ? `${filter.key}=${filter.value.tariffDataValue}` : null,
                filter.value.tariffDataUnitValue ? `${filter.value.tariffDataUnit}=${filter.value.tariffDataUnitValue}` : null,
                filter.value.tariffNameValue ? `${filter.value.tariffName}=${filter.value.tariffNameValue}` : null
            ] : null;
            const simOfferValuesFiltered = simOfferValues ? simOfferValues.filter(el => !!el) : null;
            return (
                // filter SIM offer
                typeof filter.value === 'object' && simOfferValuesFiltered ?
                    simOfferValuesFiltered.forEach(offerFilter =>
                        resultQuery.push(offerFilter))
                    :
                    // filter ip - Sim
                    typeof filter.value === 'object' && filter.value && filter.value.ipOptionChecked ?
                        resultQuery.push(`${filter.value.secondaryKey}=${filter.value.ipValue}`)
                        :
                        typeof filter.value === 'object' && filter.value && filter.value.simOptionChecked ?
                            resultQuery.push(`${filter.key}=${filter.value.simValue}`)
                            :
                            // filter sim data usage
                            typeof filter.value === 'object' && filter.value && filter.value.shouldConvert && filter.value.rangeOptionChecked ?
                                resultQuery.push(
                                    `${filter.key}=${(filter.value.amountValue[0] * 1048576) - 524.288}__value__${(filter.value.amountValue[1] * 1048576) + 524.288}`
                                )
                                :
                                typeof filter.value === 'object' && filter.value && filter.value.shouldConvert && filter.value.greaterThanOptionChecked ?
                                    resultQuery.push(`${filter.key}=${(filter.value.amountValue * 1048576) - 524.288}__value`)
                                    :
                                    typeof filter.value === 'object' && filter.value && filter.value.shouldConvert && filter.value.lowerThanOptionChecked ?
                                        resultQuery.push(`${filter.key}=value__${(filter.value.amountValue * 1048576) + 524.288}`)
                                        :
                                        typeof filter.value === 'object' && filter.value && filter.value.shouldConvert && filter.value.amountOptionChecked ?
                                            resultQuery.push( // eslint-disable-next-line max-len
                                                `${filter.key}=${(filter.value.amountValue * 1048576) - 524.288}__value__${(filter.value.amountValue * 1048576) + 524.288}`
                                            )
                                            :
                                            // filter amount
                                            typeof filter.value === 'object' && filter.value && filter.value.rangeOptionChecked ?
                                                resultQuery.push(`${filter.key}=${filter.value.amountValue[0]}__value__${filter.value.amountValue[1]}`)
                                                :
                                                typeof filter.value === 'object' && filter.value && filter.value.greaterThanOptionChecked ?
                                                    resultQuery.push(`${filter.key}=${filter.value.amountValue}__value`)
                                                    :
                                                    typeof filter.value === 'object' && filter.value && filter.value.lowerThanOptionChecked ?
                                                        resultQuery.push(`${filter.key}=value__${filter.value.amountValue}`)
                                                        :
                                                        typeof filter.value === 'object' && filter.value && filter.value.amountOptionChecked ?
                                                            resultQuery.push(`${filter.key}=${filter.value.amountValue}`)
                                                            :
                                                            //filter dates
                                                            typeof filter.value === 'object' && filter.value &&
                                                                (filter.value.startDateSelected && filter.value.isRestricted
                                                                    || (filter.value.startDateSelected
                                                                        && !filter.value.isRestricted && !filter.value.beforeSelected && !filter.value.afterSelected)) ?
                                                                resultQuery.push(
                                                                    `${filter.key}=${filter.value.startDateSelected}_value_${filter.value.endDateSelected}`)
                                                                :
                                                                typeof filter.value === 'object' && filter.value &&
                                                                    !filter.value.isRestricted && filter.value.beforeSelected ?
                                                                    resultQuery.push(`${filter.key}=value_${filter.value.endDateSelected}`)
                                                                    :
                                                                    typeof filter.value === 'object' && filter.value &&
                                                                        !filter.value.isRestricted && filter.value.afterSelected ?
                                                                        resultQuery.push(`${filter.key}=${filter.value.startDateSelected}_value`)
                                                                        :
                                                                        resultQuery.push(`${filter.key}=${filter.value}`)
            );
        });
    }

    if (isInteger(limit)) {
        resultQuery.push(`limit=${limit}`);
    }

    if (isInteger(offset)) {
        resultQuery.push(`offset=${offset}`);
    }

    Object.keys(query).forEach(additionalQueryPartKey => {
        resultQuery.push(`${additionalQueryPartKey}=${query[additionalQueryPartKey]}`);
    });
    const plusSignInQuery = /\+/gi;
    const queryToAppend = resultQuery.join('&').replace(plusSignInQuery, '%2b');

    return queryToAppend ? `${splitQuery[0]}?${queryToAppend}` : splitQuery[0];
};

/**
 * Extract query keys and values from URL
 * @param {String} queryOrUrl query parameters
 * @returns {Object} query keys and values
 */
const extractQueriesFromUrl = (queryOrUrl) => {
    checkUrlIsString(queryOrUrl);
    const resultQuery = {};
    if (queryOrUrl !== '') {
        const queryHasQuestionMark = queryOrUrl.includes('?');
        const queryHasEqual = queryOrUrl.includes('=');
        queryOrUrl.split('&')
            .forEach(keyValue => {
                const indexOfFirstEqual = keyValue.indexOf('=');
                const valueToExtract = keyValue.slice(indexOfFirstEqual + 1);
                if (queryHasEqual && queryHasQuestionMark) {
                    const indexOfQuestionMark = keyValue.indexOf('?');
                    resultQuery[keyValue.slice(indexOfQuestionMark + 1, indexOfFirstEqual)] = valueToExtract.replace('%20', ' ');
                } else if (queryHasEqual) {
                    resultQuery[keyValue.slice(0, indexOfFirstEqual)] = valueToExtract.replace('%20', ' ');
                }
            });
    }
    return resultQuery;
};

const filterObject = (obj, predicate) =>
    Object.keys(obj)
        .filter(key => predicate(key))
        .reduce((res, key) => ({
            ...res,
            [key]: obj[key]
        }), {});

const request = async (options) => {
    const onSuccess = (response) => {
        const { data } = response;
        return data;
    };

    const onError = function (error) {
        return Promise.reject({
            message: error.message,
            code: error.code,
            response: error.response
        });
    };

    return client(options).then(onSuccess).catch(onError);
};

export { buildUrl, replaceParameters, extractQueriesFromUrl, sanitizeValue, filterObject, request };